import axios from "axios";
import { SwayyAPIResponse } from "../../../models/NetworkResponse";

/** Re-export shared Swayy envelope (Orders already use this). */
export type { SwayyAPIResponse };

export class SwayyApiError extends Error {
  status?: number;
  /** Business / validation code from API when present (e.g. OPTIONS_LOCKED). */
  code?: string;
  /** Structured validation issues from API (`data.details`). */
  details: Array<{ path: string; message: string }>;

  constructor(
    message: string,
    status?: number,
    details: Array<{ path: string; message: string }> = [],
    code?: string,
  ) {
    super(message);
    this.name = "SwayyApiError";
    this.status = status;
    this.details = details;
    this.code = code;
  }
}

interface SwayyValidationDetail {
  path?: string | Array<string | number>;
  message?: string;
}

const normalizeDetailPath = (path: SwayyValidationDetail["path"]): string => {
  if (Array.isArray(path)) {
    return path.map(String).join(".");
  }
  return String(path || "");
};

const getValidationDetails = (
  body: unknown,
): Array<{ path: string; message: string }> => {
  const details = (body as { data?: { details?: SwayyValidationDetail[] } })
    ?.data?.details;
  if (!Array.isArray(details)) return [];
  return details
    .map((detail) => ({
      path: normalizeDetailPath(detail.path),
      message: detail.message?.trim() || "",
    }))
    .filter((detail) => Boolean(detail.message));
};

const getValidationDetailMessage = (body: unknown): string =>
  getValidationDetails(body)
    .map((detail) => detail.message)
    .join(" ");

const getApiErrorCode = (body: unknown): string | undefined => {
  if (!body || typeof body !== "object") return undefined;
  const record = body as Record<string, unknown>;
  const nested =
    record.data && typeof record.data === "object"
      ? (record.data as Record<string, unknown>)
      : null;
  const candidates = [record.code, nested?.code];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }
  return undefined;
};

export const assertSuccess = <T extends { success: boolean; message: string }>(
  response: T,
): T => {
  if (!response.success) {
    const message =
      getValidationDetailMessage(response) ||
      response.message ||
      "The request was unsuccessful.";
    throw new SwayyApiError(
      message,
      undefined,
      getValidationDetails(response),
      getApiErrorCode(response),
    );
  }
  return response;
};

/** Read `data` (or `result`) from a successful SwayyAPIResponse. */
export const getResponseData = <T>(response: SwayyAPIResponse<T>): T => {
  const payload = response.data ?? response.result;
  if (payload === undefined) {
    throw new SwayyApiError(
      response.message || "Response data was empty.",
    );
  }
  return payload;
};

export const normalizeSwayyApiError = (
  error: unknown,
  fallbackMessage: string,
): SwayyApiError => {
  if (error instanceof SwayyApiError) return error;

  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data as
      | {
          message?: string;
          code?: string;
        }
      | undefined;
    const details = getValidationDetails(responseData);
    const detailMessage = details.map((item) => item.message).join(" ");
    const message =
      detailMessage ||
      responseData?.message ||
      error.message ||
      fallbackMessage;
    return new SwayyApiError(
      message,
      error.response?.status,
      details,
      getApiErrorCode(responseData),
    );
  }

  return new SwayyApiError(
    error instanceof Error ? error.message : fallbackMessage,
  );
};

export const isCancelledSwayyRequest = (error: unknown): boolean =>
  axios.isCancel(error) ||
  (error instanceof Error && error.name === "CanceledError");

export const pickString = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed || null;
};

export const pickNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/** Normalize API flags (boolean or MySQL 0/1). */
export const asBool = (value: unknown, fallback = true): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  return fallback;
};

/**
 * Catalog list endpoints return `data` as a bare array
 * (`{ success, message, data: T[] }`).
 */
export const requireRowArray = (data: unknown): unknown[] => {
  if (!Array.isArray(data)) {
    throw new SwayyApiError("Expected a list in the API response data.");
  }
  return data;
};
